/**
 * TierCompare — two-column comparison (pilot vs. operated, no pricing).
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface TierCompareProps {
  namespace?: string;
  className?: string;
}

const ROWS = ['row1', 'row2', 'row3'] as const;

export function TierCompare({ namespace = 'marketing.diagrams.tierCompare', className }: TierCompareProps) {
  const t = useTranslations(namespace);
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div role="img" aria-label={t('title')} className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-stretch gap-3">
          <div className="min-w-0 rounded-xl border border-white/10 bg-white/5 p-4 text-center text-xs font-semibold uppercase tracking-wider text-white/60">
            {t('colA')}
          </div>
          <div aria-hidden="true" />
          <div className="min-w-0 rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-4 text-center text-xs font-semibold uppercase tracking-wider text-cyan-300 shadow-glow-cyan-sm">
            {t('colB')}
          </div>
          {ROWS.map((row) => (
            <div key={row} className="contents">
              <div className="flex min-w-0 items-center rounded-lg border border-white/5 bg-white/[0.02] px-4 py-3 text-sm text-white/70">
                {t(`rows.${row}.a`)}
              </div>
              <div className="flex flex-col items-center justify-center">
                <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                  {t(`rows.${row}.label`)}
                </span>
                <ArrowRight size={16} aria-hidden="true" className="mt-1 text-cyan-400" />
              </div>
              <div className="flex min-w-0 items-center rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-sm font-medium text-white/90">
                {t(`rows.${row}.b`)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </MotionReveal>
  );
}
